/**
 * Ad Performance Table Component
 * Shows UTM campaign performance metrics
 */

import { ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { AdPerformanceData } from '@/lib/analytics'

interface AdPerformanceTableProps {
  data: AdPerformanceData[] | undefined
  isLoading: boolean
  onViewAll?: () => void
}

function formatNumber(num: number): string {
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function getSourceBadgeVariant(source: string): 'default' | 'secondary' | 'outline' {
  const lowercaseSource = source.toLowerCase()
  if (lowercaseSource === 'google' || lowercaseSource === 'gclid') {
    return 'default'
  }
  if (lowercaseSource === 'facebook' || lowercaseSource === 'meta') {
    return 'secondary'
  }
  return 'outline'
}

function formatSourceDisplay(source: string): string {
  const mapping: Record<string, string> = {
    google: 'Google Ads',
    gclid: 'Google Ads',
    facebook: 'Facebook',
    meta: 'Meta Ads',
    bing: 'Bing Ads',
    direct: 'Direct'
  }
  return mapping[source.toLowerCase()] || source
}

export function AdPerformanceTable({ data, isLoading, onViewAll }: AdPerformanceTableProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ad Performance</CardTitle>
          <CardDescription>Track your advertising campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ad Performance</CardTitle>
          <CardDescription>Track your advertising campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[200px] text-center">
            <ExternalLink className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-muted-foreground mb-2">No ad traffic detected</p>
            <p className="text-xs text-muted-foreground">
              Add UTM parameters to your ad links to track campaign performance
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Show top 5, with option to view all
  const displayData = data.slice(0, 5)
  const hasMore = data.length > 5

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Ad Performance
          <Badge variant="outline" className="font-normal">
            {data.length} campaigns
          </Badge>
        </CardTitle>
        <CardDescription>Performance of your advertising campaigns</CardDescription>
      </CardHeader>
      <CardContent className="px-0 sm:px-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Visitors</TableHead>
                <TableHead className="text-right">Bookings</TableHead>
                <TableHead className="text-right">Conv.</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={getSourceBadgeVariant(row.utm_source)}>
                          {formatSourceDisplay(row.utm_source)}
                        </Badge>
                        {row.utm_medium && (
                          <span className="text-xs text-muted-foreground">
                            / {row.utm_medium}
                          </span>
                        )}
                      </div>
                      {row.utm_campaign && (
                        <span className="text-sm font-medium truncate max-w-[200px]">
                          {row.utm_campaign}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber(row.visitors)}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.bookings}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={row.conversionRate >= 3 ? 'text-green-600 font-medium' : ''}>
                      {row.conversionRate.toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(row.revenue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {hasMore && onViewAll && (
          <div className="mt-4 text-center">
            <Button variant="outline" size="sm" onClick={onViewAll}>
              View All {data.length} Campaigns
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
